import React, { memo, useRef } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableHead,
} from "@mui/material";
import EmployeeSection from "./EmployeeSection";
import dayjs from "dayjs";

const AttendanceTable = memo(function AttendanceTable({
  refContainer,
  monthData,
  filteredKeys,
  visibleDays,
  hideRulesInfos,
  selectedLeaves,
  selectedTimingFilter,
  leaveOptionsCache,
  restLeavesByEmp,
  onCellDoubleClick,
  reportSettings,
}) {
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const getContainer = () => {
    if (refContainer?.current) return refContainer.current;
    return null;
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;

    const container = getContainer();
    if (!container) return;

    dragRef.current = {
      isDragging: true,
      startX: e.pageX,
      startY: e.pageY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };

    container.style.cursor = "grabbing";
    container.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    const container = getContainer();
    if (!container || !dragRef.current.isDragging) return;

    e.preventDefault();

    const dx = e.pageX - dragRef.current.startX;
    const dy = e.pageY - dragRef.current.startY;

    container.scrollLeft = dragRef.current.scrollLeft - dx;
    container.scrollTop = dragRef.current.scrollTop - dy;
  };

  const stopDragging = () => {
    const container = getContainer();
    dragRef.current.isDragging = false;

    if (!container) return;
    container.style.cursor = "grab";
    container.style.userSelect = "";
  };

  return (
    <TableContainer
      ref={refContainer}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDragging}
      onMouseLeave={stopDragging}
      sx={{
        overflow: "auto",
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,.08)",
        maxHeight: "75vh",
        cursor: "grab",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {/* Fixed corner cell — sits above the employee-name column */}
            <TableCell
              sx={{
                minWidth: 220,
                fontWeight: 800,
                bgcolor: "#e0f2f1",
                color: "#004d40",
                position: "sticky",
                left: 0,
                top: 0,
                zIndex: 5,
                
              }}
            >
              {hideRulesInfos ? "Employee / Attendance" : "Employee"}
            </TableCell>

            {/* ONE set of day labels for the whole table — this row itself
                is already sticky via MUI's stickyHeader on <Table>, so it
                stays fixed at the top as you scroll down through employees */}
            {visibleDays.map((iso) => {
              const d = dayjs(iso);
              return (
                <TableCell
                  key={iso}
                  align="center"
                  sx={{
                    minWidth: 110,
                    fontWeight: 800,
                    bgcolor: "#f7faf9",
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                  }}
                >
                  <div>{d.format("ddd")}</div>
                  <div style={{ fontSize: 11, opacity: 0.8 }}>
                    {d.format("DD/MM")}
                  </div>
                </TableCell>
              );
            })}

            <TableCell
              align="center"
              sx={{
                minWidth: 100,
                fontWeight: 800,
                position: "sticky",
                top: 0,
                zIndex: 4,
              }}
            >
              Totals
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredKeys.map((empKey) => (
            <EmployeeSection
              key={empKey}
              empKey={empKey}
              emp={monthData[empKey]}
              visibleDays={visibleDays}
              hideRulesInfos={hideRulesInfos}
              selectedLeaves={selectedLeaves}
              selectedTimingFilter={selectedTimingFilter}
              leaveOptionsCache={leaveOptionsCache}
              restLeavesByEmp={restLeavesByEmp}
              onCellDoubleClick={onCellDoubleClick}
              reportSettings={reportSettings}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default AttendanceTable;
     


 